import React, { useMemo } from 'react';
import { Button, Typography, Row, Col, Space, Divider, Alert } from 'antd';
import { UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import AttachmentUploadCard from './AttachmentUploadCard';

const { Text } = Typography;

const AttachmentsSection = ({
    attachmentTypes,
    uploadProps,
    uploadLoading,
    fileList,
    studentProfileData,
    uploadProgress,
    uploadStatus,
    onFileSelect,
    onManualUpload,
    onBatchUpload,
    validationErrors = {}
}) => {
    // Filter attachment types based on student profile data (memoized)
    const filteredAttachmentTypes = useMemo(() => {
        return attachmentTypes.filter(type => {
            // Show IELTS only if student has entered IELTS score
            if (type.code === 'IELTS') {
                return studentProfileData?.ielts_grade && studentProfileData.ielts_grade !== '';
            }
            
            // Show PRIORITY only if student has entered ĐẦY ĐỦ thông tin giải tỉnh
            if (type.code === 'PRIORITY') {
                return studentProfileData?.provincial_prize && 
                       studentProfileData.provincial_prize !== '' &&
                       studentProfileData?.provincial_prize_subject_id && 
                       studentProfileData.provincial_prize_subject_id !== '' &&
                       studentProfileData?.provincial_prize_grade && 
                       studentProfileData.provincial_prize_grade !== '';
            }
            
            // Show all other types (FRONT_ID_CARD, BACK_ID_CARD, TRANSCRIPT)
            return true;
        });
    }, [
        attachmentTypes,
        studentProfileData?.ielts_grade,
        studentProfileData?.provincial_prize,
        studentProfileData?.provincial_prize_subject_id,
        studentProfileData?.provincial_prize_grade
    ]);

    // Check if attachment is required based on backend is_required flag + conditional rules
    const isAttachmentRequired = (type) => {
        // IELTS: conditional — bắt buộc khi đã nhập điểm IELTS
        if (type.code === 'IELTS') {
            return !!(studentProfileData?.ielts_grade && studentProfileData.ielts_grade !== '');
        }
        // PRIORITY: conditional — bắt buộc khi đã nhập đầy đủ thông tin giải tỉnh
        if (type.code === 'PRIORITY') {
            return !!(studentProfileData?.provincial_prize &&
                   studentProfileData.provincial_prize !== '' &&
                   studentProfileData?.provincial_prize_subject_id &&
                   studentProfileData.provincial_prize_subject_id !== '' &&
                   studentProfileData?.provincial_prize_grade &&
                   studentProfileData.provincial_prize_grade !== '');
        }
        // Các loại còn lại: dùng cờ is_required từ backend
        // (Cũ: cứng trong frontend — return typeCode === 'FRONT_ID_CARD' || typeCode === 'BACK_ID_CARD')
        // (Cũ: TRANSCRIPT chỉ bắt buộc khi graduation_year < 2026)
        return !!type.is_required;
    };

    // Calculate statistics
    const stats = useMemo(() => {
        let total = filteredAttachmentTypes.length;
        let uploaded = 0;
        let pending = 0;
        let required = 0;

        filteredAttachmentTypes.forEach(type => {
            if (isAttachmentRequired(type)) {
                required++;
            }

            const files = fileList[type.id];
            if (files && files.length > 0) {
                const file = files[0];
                if (file.status === 'done' || uploadStatus?.[type.id] === 'success') {
                    uploaded++;
                } else {
                    pending++;
                }
            }
        });

        return { total, uploaded, pending, required };
    }, [filteredAttachmentTypes, fileList, uploadStatus]);

    const allRequiredUploaded = useMemo(() => {
        return filteredAttachmentTypes
            .filter(type => isAttachmentRequired(type))
            .every(type => {
                const files = fileList[type.id];
                return files && files.length > 0 && 
                    (files[0].status === 'done' || uploadStatus?.[type.id] === 'success');
            });
    }, [filteredAttachmentTypes, fileList, uploadStatus]);

    const hasPendingUploads = useMemo(() => {
        return filteredAttachmentTypes.some(type => {
            const files = fileList[type.id];
            return files && files.length > 0 && 
                files[0].status !== 'done' && 
                uploadStatus?.[type.id] !== 'success';
        });
    }, [filteredAttachmentTypes, fileList, uploadStatus]);

    return (
        <div>
            {/* Statistics Summary */}
            <Alert
                message={
                    <Space>
                        <Text strong>Trạng thái tải file: </Text>
                        <Text>
                            Đã tải: <Text type="success" strong>{stats.uploaded}</Text> / {stats.total}
                        </Text>
                        {stats.required > 0 && (
                            <Text>
                                | Bắt buộc: <Text type={allRequiredUploaded ? 'success' : 'danger'} strong>
                                    {stats.required}
                                </Text>
                            </Text>
                        )}
                    </Space>
                }
                type={allRequiredUploaded ? 'success' : 'warning'}
                icon={allRequiredUploaded && <CheckCircleOutlined />}
                showIcon
                style={{ marginBottom: 16 }}
            />

            {/* Upload Cards Grid */}
            <Row gutter={[16, 16]}>
                {filteredAttachmentTypes.map(type => (
                    <Col xs={24} sm={12} md={8} key={type.id}>
                        <AttachmentUploadCard
                            type={type}
                            isRequired={isAttachmentRequired(type)}
                            fileList={fileList[type.id] || []}
                            uploadLoading={uploadLoading[type.id]}
                            uploadProgress={uploadProgress?.[type.id]}
                            uploadStatus={uploadStatus?.[type.id]}
                            onFileSelect={(file) => onFileSelect && onFileSelect(type.id, file)}
                            onRemove={() => {
                                const currentFile = fileList[type.id]?.[0];
                                if (currentFile && uploadProps(type.id).onRemove) {
                                    return uploadProps(type.id).onRemove(currentFile);
                                }
                            }}
                            onUpload={(file) => onManualUpload && onManualUpload(type.id, file)}
                            validationError={validationErrors[type.id]}
                        />
                    </Col>
                ))}
            </Row>

            <Divider />
            
            {/* Important Notes */}
            <Alert
                message={<Text strong>Lưu ý quan trọng:</Text>}
                description={
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>
                            <strong>Ảnh CCCD 2 mặt:</strong> Bắt buộc với tất cả thí sinh
                        </li>
                        <li>
                            <strong>Ảnh học bạ:</strong> Bắt buộc với tất cả thí sinh
                        </li>
                        <li>
                            <strong>Chứng chỉ IELTS:</strong> Chỉ hiển thị và bắt buộc khi đã nhập điểm IELTS trong hồ sơ
                        </li>
                        <li>
                            <strong>Giải thưởng cấp tỉnh:</strong> Chỉ hiển thị và bắt buộc khi đã chọn đầy đủ thông tin giải thưởng trong hồ sơ
                        </li>
                    </ul>
                }
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
            />
        </div>
    );
};

AttachmentsSection.propTypes = {
    attachmentTypes: PropTypes.array.isRequired,
    uploadProps: PropTypes.func.isRequired,
    uploadLoading: PropTypes.object.isRequired,
    fileList: PropTypes.object.isRequired,
    studentProfileData: PropTypes.object,
    uploadProgress: PropTypes.object,
    uploadStatus: PropTypes.object,
    onFileSelect: PropTypes.func,
    onManualUpload: PropTypes.func,
    onBatchUpload: PropTypes.func,
    validationErrors: PropTypes.object
};

// Memoize component to prevent unnecessary re-renders
// Only re-render when relevant props change
export default React.memo(AttachmentsSection, (prevProps, nextProps) => {
    // Compare fileList - check if same reference or same content
    if (prevProps.fileList !== nextProps.fileList) {
        return false; // Re-render if fileList changed
    }
    
    // Compare attachment-related fields in studentProfileData
    const prevData = prevProps.studentProfileData || {};
    const nextData = nextProps.studentProfileData || {};
    
    if (
        prevData.ielts_grade !== nextData.ielts_grade ||
        prevData.provincial_prize !== nextData.provincial_prize ||
        prevData.provincial_prize_subject_id !== nextData.provincial_prize_subject_id ||
        prevData.provincial_prize_grade !== nextData.provincial_prize_grade ||
        prevData.graduation_year !== nextData.graduation_year
    ) {
        return false; // Re-render if attachment-related fields changed
    }
    
    // Compare upload status
    if (prevProps.uploadStatus !== nextProps.uploadStatus) {
        return false; // Re-render if upload status changed
    }
    
    // Compare upload progress
    if (prevProps.uploadProgress !== nextProps.uploadProgress) {
        return false; // Re-render if upload progress changed
    }
    
    // Compare validation errors
    if (prevProps.validationErrors !== nextProps.validationErrors) {
        return false; // Re-render if validation errors changed
    }
    
    // Compare attachmentTypes length (in case types are added/removed)
    if (prevProps.attachmentTypes.length !== nextProps.attachmentTypes.length) {
        return false; // Re-render if attachment types changed
    }
    
    // Skip re-render if nothing important changed
    return true;
});
